import { useState } from "react";
import { Link } from "react-router-dom";
import { Copy, Check } from "lucide-react";
import { toast } from "sonner";
import { Kicker } from "@/components/ui/kicker";
import { Button } from "@/components/ui/button";
import { SimpleTooltip } from "@/components/ui/tooltip";
import { SeverityDot } from "@/components/ui/severity-dot";
import { ScenarioRow } from "@/components/experience/scenario-row";
import { PersonaRow } from "@/components/experience/persona-row";
import { WorkflowRail } from "@/components/workflow/workflow-rail";
import {
  FindingTriageMenu,
  ExportFindingButton,
} from "@/components/experience/finding-actions";
import type { FindingInspectorRailProps } from "./finding-detail-types";

export function FindingInspectorRail({
  id,
  findingId,
  isCanonical,
  severity,
  type,
  observationCount,
  firstSeenFormatted,
  scenarioName,
  scenarioId,
  personaName,
  personaIdForLink,
  personaRole,
  stepNumber,
  walkthroughId,
  canonicalParentId,
  stateReason,
  fixStatus: _fixStatus,
  hasLinkedRepo,
  exportConfig,
  state,
  subject,
}: FindingInspectorRailProps) {
  const shortId = findingId.length > 8 ? findingId.slice(0, 8) : findingId;
  const [copied, setCopied] = useState(false);
  const [copiedCli, setCopiedCli] = useState(false);

  const handleCopyId = () => {
    navigator.clipboard.writeText(findingId);
    setCopied(true);
    toast.success("ID copied to clipboard");
    setTimeout(() => setCopied(false), 2000);
  };

  const handleCopyCli = () => {
    if (!scenarioId) return;
    const cmd = `iamu run ${scenarioId}`;
    navigator.clipboard.writeText(cmd);
    setCopiedCli(true);
    toast.success("Reproduction command copied");
    setTimeout(() => setCopiedCli(false), 2000);
  };

  return (
    <div className="space-y-6 lg:sticky lg:top-6">
      {/* Details Card */}
      <div className="rounded-lg border border-border bg-card p-4 space-y-4">
        <div className="flex items-center justify-between border-b border-border/60 pb-3">
          <Kicker as="h3">Details</Kicker>
          <SimpleTooltip content={copied ? "Copied!" : "Copy full ID"}>
            <button
              type="button"
              onClick={handleCopyId}
              className="inline-flex items-center gap-1 font-mono text-xs text-muted-foreground hover:text-foreground transition-colors"
              aria-label="Copy full ID"
            >
              <span>{shortId}</span>
              {copied ? (
                <Check className="h-3 w-3 text-primary" />
              ) : (
                <Copy className="h-3 w-3" />
              )}
            </button>
          </SimpleTooltip>
        </div>

        <dl className="grid grid-cols-[5.5rem_minmax(0,1fr)] gap-x-2 gap-y-3 text-sm items-baseline">
          <dt className="text-muted-foreground">Severity</dt>
          <dd className="inline-flex items-center gap-1.5 font-medium">
            <SeverityDot
              tone={
                severity === "critical"
                  ? "critical"
                  : severity === "major"
                    ? "important"
                    : "minor"
              }
            />
            {severity.charAt(0).toUpperCase() + severity.slice(1)}
          </dd>

          {isCanonical && observationCount > 1 && (
            <>
              <dt className="text-muted-foreground">Occurrences</dt>
              <dd className="tabular-nums font-medium">{observationCount}×</dd>
            </>
          )}

          {firstSeenFormatted && (
            <>
              <dt className="text-muted-foreground">
                {isCanonical ? "First seen" : "Observed"}
              </dt>
              <dd className="tabular-nums text-muted-foreground">
                {firstSeenFormatted}
              </dd>
            </>
          )}

          {scenarioName && scenarioId && (
            <>
              <dt className="text-muted-foreground">Scenario</dt>
              <dd className="break-words">
                <ScenarioRow
                  variant="inline"
                  scenario={{ id: scenarioId, name: scenarioName }}
                  href={`/projects/${id}/scenarios/${scenarioId}`}
                />
              </dd>
            </>
          )}

          {personaName && (
            <>
              <dt className="text-muted-foreground">Persona</dt>
              <dd className="break-words">
                <PersonaRow
                  variant="inline"
                  persona={{
                    id: personaIdForLink ?? personaName,
                    name: personaName,
                    role: personaRole,
                  }}
                  href={
                    personaIdForLink
                      ? `/projects/${id}/personas/${personaIdForLink}`
                      : undefined
                  }
                  size="xs"
                />
              </dd>
            </>
          )}

          {typeof stepNumber === "number" && walkthroughId && (
            <>
              <dt className="text-muted-foreground">Step</dt>
              <dd>
                <Link
                  to={`/projects/${id}/walkthroughs/${walkthroughId}?tab=trace&step=${stepNumber}`}
                  className="underline-offset-4 hover:text-foreground hover:underline"
                >
                  Step {stepNumber}
                </Link>
              </dd>
            </>
          )}
        </dl>
      </div>

      {/* Reproduction / CLI Agent Command */}
      {scenarioId && (
        <div className="rounded-lg border border-border bg-card p-4 space-y-2.5">
          <Kicker as="h3">Reproduce in agent</Kicker>
          <p className="text-xs text-muted-foreground">
            Execute this scenario in your terminal or AI coding agent to verify
            fixes.
          </p>
          <div className="flex items-center gap-1.5">
            <code className="min-w-0 flex-1 truncate rounded bg-muted px-2 py-1 font-mono text-xs text-foreground select-all">
              iamu run {scenarioId}
            </code>
            <SimpleTooltip content={copiedCli ? "Copied!" : "Copy command"}>
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="h-7 px-2 shrink-0"
                onClick={handleCopyCli}
                aria-label="Copy CLI reproduction command"
              >
                {copiedCli ? (
                  <Check className="h-3.5 w-3.5 text-primary" />
                ) : (
                  <Copy className="h-3.5 w-3.5" />
                )}
              </Button>
            </SimpleTooltip>
          </div>
        </div>
      )}

      {/* Pattern banner for raw observation */}
      {!isCanonical && canonicalParentId && (
        <div className="rounded-lg border border-border bg-surface-tonal p-4 space-y-2.5">
          <Kicker as="h3">Recurring pattern</Kicker>
          <p className="text-xs leading-relaxed text-muted-foreground">
            This observation is grouped under a recurring pattern across
            walkthroughs.
          </p>
          <Button asChild variant="outline" size="sm" className="w-full">
            <Link to={`/projects/${id}/findings/${canonicalParentId}`}>
              See canonical finding →
            </Link>
          </Button>
        </div>
      )}

      {/* Canonical Workflow Rail */}
      {isCanonical && (
        <WorkflowRail
          canonicalId={findingId}
          projectId={id}
          stateReason={stateReason}
          canonicalState={state}
        />
      )}

      {/* Raw finding actions */}
      {!isCanonical && (
        <div className="rounded-lg border border-border bg-card p-4 space-y-3">
          <Kicker as="h3">Actions</Kicker>
          <div className="flex flex-col gap-2">
            <FindingTriageMenu
              findingId={findingId}
              projectId={id}
              state={state}
              findingType={type}
              subject={subject ?? undefined}
              className="w-full justify-between"
            />
            {exportConfig && (
              <ExportFindingButton
                findingId={findingId}
                projectId={id}
                exportConfig={exportConfig}
                hasLinkedRepo={hasLinkedRepo}
              />
            )}
          </div>
        </div>
      )}
    </div>
  );
}
